/**
 * Die Morse-Lernseite — der Rahmen. (06.10.2026, Morse-Modul Bahn 1; überarbeitet 06.10.2026)
 *
 * Martin: „Morse-Code geht schon ganz gut, aber gehört jede Seite bitte komplett überarbeitet. Ich kann Geben
 * und Hören noch nicht so ganz nutzen. Start und Stopp geht noch nicht so ganz.“ Belegt: keine einzige Übung
 * gebucht. Die Diagnose steht in den Reitern (morse-hoeren.ts, morse-geben.ts) und im Ton (morse-ton.ts).
 *
 * Der Rahmen:
 *   - im Kopf „Wie funktioniert das?“ — darunter die Einführung (morse-einfuehrung.ts), beim ersten Öffnen offen;
 *   - oben der Stand in EINER Zeile: Stufe, neues Zeichen (antippen = anhören), Sicherheit;
 *   - vier Reiter (WAI-ARIA Tabs, Pfeiltasten): Hören, Geben, Tabelle, Fortschritt — jeder beginnt mit ein, zwei
 *     Sätzen, was man dort tut (morse-texte.ts). Hören und Geben haben dieselbe Bühne mit EINEM Hauptknopf
 *     „Start“ ↔ „Stopp“ (morse-teile.ts) und oben rechts den Schnellzugriff „Tabelle“: die Übung pausiert,
 *     „Zurück zur Übung“ führt hin und setzt sie fort (06.10.2026, Martin: „… fehlt auch eine Tabelle“);
 *   - darunter eingeklappt „Tempo und Ton“ — für alle Reiter derselbe Block;
 *   - Tastatur: Leertaste = Start/Stopp (läuft das Geben, morst sie), Esc = Stopp. Die Leertaste gehört hier der
 *     Seite: app.ts startet mit ihr sonst die Sprechtaste (Push-to-Talk), sobald kein Bedienelement den Fokus hat.
 *   - Abgang (Seite ausgeblendet, App verdeckt): eine laufende Übung pausiert, der Ton endet (Prüfbefund W2).
 *   - Diagnose: kurze Zeilen „[morse] …“ ans Server-Protokoll (mikro-spur.ts, `meldeSpur`) — Start, Stopp,
 *     Pause, Zustand des Tons, Fehler. NIE Inhalte (keine Zeichen, nichts Getipptes).
 *
 *   - Hörspur (07.10.2026, morse-hoerspur.ts): „Vorführung anhören“ (Hören/Bereit, Einführung) und „Alle der Reihe
 *     nach“ (Tabelle). Die Seite hält sie an, wo etwas anderes Ton braucht: Reiterwechsel, andere Ansicht, Seite im
 *     Hintergrund (dazu gehört alles, was in den Chat springt: Sprechtaste, Freihand, Wach-Wort, Anruf), Start einer
 *     Übung, Antippen eines Zeichens, die Taste, eine Tagesaufnahme, die BEGINNT, während sie spielt, eine vorgelesene
 *     Antwort (Ereignisse der App). Öffnet man sie aus der Einführung während einer laufenden Übung, pausiert die
 *     Übung — „Zurück zur Übung“.
 *   - Eine LAUFENDE Tagesaufnahme hält für die Hörspur weich an und läuft danach weiter (morse-spur-halt.ts, 07.10.2026,
 *     Martin: „Der Button geht nicht.“); nur die Sprechtaste lässt sie gar nicht erst spielen.
 *
 * Alles am Konto (server/morse-routen.ts); der Server wertet Soll gegen Ist selbst aus.
 * ⚠️ Keine Inline-Styles: alles in style.css (`.morse-…`).
 */
import { KOCH_LETZTE_STUFE, kochNeueZeichen, kochTempoVorgabe, type Umfang } from "../../shared/morse-koch.js";
import { MORSE_PFADE } from "../../shared/morse-konto.js";
import { langsamesTempo, MORSE_GRENZEN, MORSE_TABELLE, pruefeMorseEinstellungen, zeitplan, type MorseEinstellungen } from "../../shared/morse.js";
import { meldeSpur } from "./mikro-spur.js";
import { musterBild } from "./morse-bild.js";
import { MorseEinfuehrung } from "./morse-einfuehrung.js";
import { MorseFortschritt } from "./morse-fortschritt.js";
import { MorseGeben } from "./morse-geben.js";
import { MorseHoeren } from "./morse-hoeren.js";
import type { DiktatAnschluss } from "./morse-sprechen.js";
import { diktatAusApp } from "./morse-sprechen-diktat.js";
import { quittungPlan, quittungTon, WEITER_LAUTSTAERKE, type QuittungsArt } from "./morse-augen-logik.js";
import { stimmeAusApp } from "./morse-stimme.js";
import { HoerspurDienst } from "./morse-hoerspur-ansicht.js";
import { MorseHoerspur } from "./morse-hoerspur.js";
import { TagesaufnahmeHalt, tagesaufnahmeAusApp, type TagesaufnahmeAnschluss } from "./morse-spur-halt.js";
import { seitenAufnahmeLaeuft } from "./playback-hifi.js";
import { stufenSicherheit } from "./morse-runde.js";
import { MorseTabelle } from "./morse-tabelle.js";
import { HOERSPUR, REITER_NAME, REITER_TEXT, STUFE, TABELLE, TEMPO } from "./morse-texte.js";
import {
  baueMeldung,
  bedientLeertasteSelbst,
  el,
  gueteBalken,
  hoereMitMeldung,
  keinTonText,
  knopf,
  meldeIn,
  musterSprechen,
  prozent,
  type BeimStart,
  type Buchung,
  type BuchungsAntwort,
  type MorseBuchungsArt,
  type MorseReiter,
  type MorseStandDaten,
  type StimmeLauf,
  type MorseUebung,
  type MorseUmgebung,
  type StufeAnschluss,
  type StufeErgebnis,
} from "./morse-teile.js";
import { morseSpieler, type MorseSpieler, type MorseSpielErgebnis } from "./morse-ton.js";
import { buildPageHeader } from "./sheet-ui.js";
import { playBeep } from "./audio-feedback.js";
import { baueAufstiegKarte, fanfareErlaubt } from "./morse-aufstieg.js";
import { baueStufenLeiste, baueZiel, zielAnsicht } from "./morse-ziel.js";

export type { MorseReiter, MorseStandDaten } from "./morse-teile.js";

/** Die Reiter in ihrer Reihenfolge — Namen und Beschreibungen aus morse-texte.ts. */
export const REITER: ReadonlyArray<{ id: MorseReiter; titel: string }> = (["hoeren", "geben", "tabelle", "fortschritt"] as const).map((id) => ({
  id,
  titel: REITER_NAME[id],
}));
/** Wo Martin zuletzt war — nur eine Bequemlichkeit des Geräts. */
const REITER_SCHLUESSEL = "vespera.morse.reiter";

/**
 * Ist das ein Stand, wie ihn der Server schickt? Eine kaputte oder unerwartete Antwort darf die Seite nicht leeren
 * (Prüfbefund B1: eine Buchungsantwort ohne `stand` setzte den Stand auf undefined, danach warf jede Zeichnung).
 */
function istStand(x: unknown): x is MorseStandDaten {
  const s = x as Partial<MorseStandDaten> | null | undefined;
  return !!s && typeof s === "object" && typeof s.stufe === "number" && !!s.einstellungen && Array.isArray(s.zeichen) && Array.isArray(s.sitzungen);
}

interface MorseViewDeps {
  fetch?: typeof fetch;
  spieler?: MorseSpieler;
  /** Zeit für Dauer und Tastendruck (ms). */
  jetzt?: () => number;
  zufall?: () => number;
  /** Diagnosezeile (ohne „[morse] “) — Vorgabe: ins Server-Protokoll. */
  spur?: (text: string) => void;
  /**
   * Diagnosezeile des Halts der Tagesaufnahme (ohne „[morse-spur] “) — Vorgabe: ins Server-Protokoll; ist `spur`
   * gesetzt (Tests), dorthin mit „[morse-spur] “ davor.
   */
  spurHalt?: (text: string) => void;
  /** Nimmt die Sprechtaste gerade in der Seite auf? Vorgabe: die Tonanlage (playback-hifi.ts). */
  sprechtaste?: () => boolean;
  /** Die Tagesaufnahme (anhalten, fortsetzen). Vorgabe: native-recorder.ts über morse-spur-halt.ts. */
  tagesaufnahme?: TagesaufnahmeAnschluss;
  /** Mikrofon und Mitschrift für „Sprechen“ im Reiter Hören. Vorgabe: das Live-Diktat der App; `null`: ohne den Knopf. */
  diktat?: DiktatAnschluss | null;
  /** Die kurze Stimme für „Augen zu“. Vorgabe: der TTS-Weg der App (morse-stimme.ts); `null`: ohne Stimme. */
  stimme?: ((text: string) => StimmeLauf) | null;
  /** Messzeile `[morse-sprechen] …` (ohne den Bereich) — Vorgabe: ins Server-Protokoll; ist `spur` gesetzt (Tests), dorthin mit Bereich. */
  messe?: (text: string) => void;
}

/**
 * Die Ereignisse der App, mit denen die TAGESAUFNAHME beginnt (ptt.ts meldet `mikro-bedarf {aktiv}` nur dort) — dann
 * schweigt die Hörspur. ⚠️ Die Sprechtaste, das Freihand-Mikro, das Wach-Wort und der Anruf schicken dieses Ereignis
 * NICHT: sie springen in den Chat (app.ts `switchToTab("chat")`), die Morse-Seite wird verborgen und hält sich selbst
 * an (MutationObserver auf `hidden` → `ruhe`). Dazu prüft die Hörspur vor jedem Schritt die Sprechtaste.
 *
 * ⚠️ 07.10.2026: `recorder-state` kommt bei JEDEM Abgleich mit dem Native (syncFromNative), auch ohne Wechsel — und
 *    der Halt gleicht selbst ab, bevor er anhält (`pauseDayRecorderForMic`). Darum zählt das Ereignis nur, solange die
 *    Spur SPIELT und der Halt nicht gerade greift; eine pausierte Spur hält beim „Weiter“ neu an.
 */
const AUFNAHME_EREIGNISSE = ["voice-v8:recorder-will-start", "voice-v8:recorder-state", "voice-v8:mikro-bedarf"] as const;
/** Eine vorgelesene Antwort beginnt (tts-queue.ts): zwei Töne übereinander wären Lärm — die Hörspur schweigt (Prüfbericht K1). */
const VORLESEN_EREIGNIS = "voice-v8:tts-audio-start";

export class MorseView {
  private stand: MorseStandDaten | null = null;
  private reiter: MorseReiter = "hoeren";
  private readonly holen: typeof fetch;
  private readonly spieler: MorseSpieler;
  private readonly jetzt: () => number;
  private readonly zufall: () => number;
  private readonly spurZeile: (text: string) => void;
  private zerstoert = false;
  private gebaut = false;

  private standZeile!: HTMLElement;
  /** Die Leiste der Stufen (morse-ziel.ts, 23.10.2026) — unter der Stand-Zeile, in derselben Karte. */
  private stufenPlatz!: HTMLElement;
  /** Das Ziel der Stufe (morse-ziel.ts) — unter dem Stand. */
  private zielPlatz!: HTMLElement;
  /** Die Karte nach einem Aufstieg (`feiere`). */
  private aufstiegPlatz!: HTMLElement;
  private standMeldung!: HTMLElement;
  private reiterKnoepfe = new Map<MorseReiter, HTMLButtonElement>();
  private tafeln = new Map<MorseReiter, HTMLElement>();
  private hoeren: MorseHoeren | null = null;
  private geben: MorseGeben | null = null;
  private fortschritt: MorseFortschritt | null = null;
  private tabelle: MorseTabelle | null = null;
  private einfuehrung: MorseEinfuehrung | null = null;
  private einfuehrungsPlatz: ReturnType<HoerspurDienst["vorfuehrungPlatz"]> | null = null;
  /** Die Hörspur: Abspieler, Plätze für ihre Knöpfe, „Zurück zur Übung“ (morse-hoerspur-ansicht.ts). */
  private readonly hoerspur: HoerspurDienst;
  /** „Eine Stufe zurück“ / „Stufe wählen“: der Weg zum Server und die Sperre mitten in einer Runde (morse-stufe.ts). */
  private readonly stufeAnschluss: StufeAnschluss = { setze: (stufe) => this.stufeSetzen(stufe), sperre: () => this.stufeSperre() };
  /** Woher man in die Tabelle kam — und ob die Übung dort lief (dann setzt „Zurück zur Übung“ sie fort). */
  private herkunft: { reiter: "hoeren" | "geben"; weiter: boolean } | null = null;

  // Tempo und Ton
  private regler = new Map<keyof MorseEinstellungen, { r: HTMLInputElement; wert: HTMLOutputElement; einheit: string; sprich: string }>();
  private einstellungenKurz!: HTMLElement;
  private einstellungenMeldung!: HTMLElement;
  private vorgabeZeile!: HTMLElement;
  private einstellungenBlock!: HTMLDetailsElement;
  private vorgabeKnopf!: HTMLButtonElement;
  private speicherUhr: ReturnType<typeof setTimeout> | null = null;

  // Abgang: wird die Seite ausgeblendet oder die App verdeckt, verstummt alles (Prüfbefund W2).
  private beobachter: MutationObserver | null = null;
  private readonly sichtbarkeit = (): void => {
    // Verdeckt kann iOS die Seite ohne weiteres Wort verwerfen: was beantwortet ist, geht mit keepalive (W4).
    if (typeof document !== "undefined" && document.visibilityState === "hidden") this.ruhe("verdeckt", true);
  };
  private readonly seiteGeht = (): void => this.ruhe("pagehide", true);
  /** Das Fenster verliert den Fokus (Prüfbefund K2): eine gehaltene Taste los — sonst klänge der Mithör-Ton weiter. */
  private readonly fensterWeg = (): void => this.geben?.loeseTaste?.();
  private readonly tastatur = (ev: KeyboardEvent): void => this.taste(ev);
  /** Eine vorgelesene Antwort beginnt: die Hörspur schweigt. */
  private readonly vorlesenBeginnt = (): void => this.hoerspur.stille("vorlesen");
  /**
   * Die Tagesaufnahme beginnt (oder läuft wieder), während die Hörspur spielt: die Hörspur schweigt. Ein vorgewärmtes
   * Mikro ist keine Aufnahme; die Antwort auf das eigene Anhalten auch nicht (siehe AUFNAHME_EREIGNISSE).
   */
  private readonly aufnahmeBeginnt = (ev: Event): void => {
    const d = (ev as CustomEvent<{ state?: string; aktiv?: boolean; grund?: string } | undefined>).detail;
    if (ev.type === "voice-v8:recorder-state" && d?.state !== "recording") return;
    if (ev.type === "voice-v8:mikro-bedarf" && (d?.aktiv !== true || d?.grund === "vorwaermen")) return;
    if (!this.hoerspur.engine.spielt || this.halt.greift) return;
    this.hoerspur.engine.stoppWeilAufnahme();
  };
  /** Die Tagesaufnahme für die Hörspur weich anhalten (morse-spur-halt.ts). */
  private readonly halt: TagesaufnahmeHalt;
  /** Mikrofon und Mitschrift für „Sprechen“ (morse-sprechen-diktat.ts) — `undefined`: ohne den Knopf. */
  private readonly diktat: DiktatAnschluss | undefined;
  /** Die kurze Stimme und die Messzeile für „Augen zu“ (morse-augen-zu.ts). */
  private readonly stimmeAnschluss: ((text: string) => StimmeLauf) | undefined;
  private readonly messeZeile: (text: string) => void;

  constructor(
    private readonly root: HTMLElement,
    deps: MorseViewDeps = {},
  ) {
    this.holen = deps.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
    this.spieler = deps.spieler ?? morseSpieler;
    this.jetzt = deps.jetzt ?? (() => performance.now());
    this.zufall = deps.zufall ?? Math.random;
    this.spurZeile = deps.spur ?? ((t: string) => meldeSpur("morse", t));
    this.diktat = deps.diktat === null ? undefined : (deps.diktat ?? diktatAusApp());
    this.stimmeAnschluss = deps.stimme === null ? undefined : (deps.stimme ?? stimmeAusApp());
    this.messeZeile = deps.messe ?? (deps.spur ? (t: string) => deps.spur!(`[morse-sprechen] ${t}`) : (t: string) => meldeSpur("morse-sprechen", t));
    // Ins Server-Protokoll als „[morse-spur] …“; wer die Zeilen der Seite selbst sammelt (Tests), bekommt diese mit.
    const eigeneSpur = deps.spur;
    const spurHalt = deps.spurHalt ?? (eigeneSpur ? (t: string) => eigeneSpur(`[morse-spur] ${t}`) : (t: string) => meldeSpur("morse-spur", t));
    this.halt = new TagesaufnahmeHalt({
      anschluss: deps.tagesaufnahme ?? tagesaufnahmeAusApp(),
      spur: (t) => {
        try {
          spurHalt(t);
        } catch {
          /* Diagnose ist nie wichtiger als die Aufnahme */
        }
      },
      jetzt: this.jetzt,
    });
    const knopfVon = (liste: string): string => (liste === "tabelle" ? HOERSPUR.tabelle.start : HOERSPUR.vorfuehrung.start);
    this.hoerspur = new HoerspurDienst(
      new MorseHoerspur({
        spieler: this.spieler,
        spur: (t) => this.spur(t),
        jetzt: this.jetzt,
        sprechtaste: deps.sprechtaste ?? seitenAufnahmeLaeuft,
        keinTon: (zustand) => keinTonText(zustand, "auf Abspielen"),
        sprechtasteText: (liste) => HOERSPUR.meldung.sprechtaste(knopfVon(liste)),
        halt: this.halt,
        haltText: (art, liste) => {
          const l = liste === "tabelle" ? "tabelle" : "demo";
          if (art === "zeitgrenze") return HOERSPUR.meldung.zeitgrenze;
          if (art === "nimmt-auf") return HOERSPUR.meldung.tagesaufnahmeImBrowser(l);
          if (art === "laeuft-wieder") return HOERSPUR.meldung.tagesaufnahmeLaeuftWieder(l, knopfVon(liste));
          return HOERSPUR.meldung.tagesaufnahmeNichtAngehalten(knopfVon(liste));
        },
      }),
      { pausiereUebung: () => this.pausiereUebung() },
    );
    try {
      const gemerkt = localStorage.getItem(REITER_SCHLUESSEL);
      if (REITER.some((r) => r.id === gemerkt)) this.reiter = gemerkt as MorseReiter;
    } catch {
      /* Privatmodus: dann eben Hören */
    }
    /*
     * ⚠️ Prüfbefund W2: app.ts blendet Ansichten nur aus (switchToTab setzt `hidden`), niemand ruft `destroy()`.
     *    Darum hört die Seite selbst auf ihr `hidden` — jeder Weg hinaus geht über dieses eine Attribut. Dazu das
     *    Verdecken der App (Sperrbildschirm, anderer Reiter des Browsers).
     */
    if (typeof MutationObserver !== "undefined") {
      this.beobachter = new MutationObserver(() => {
        if (this.root.hidden) this.ruhe("seite");
      });
      this.beobachter.observe(this.root, { attributes: true, attributeFilter: ["hidden"] });
    }
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.sichtbarkeit);
      document.addEventListener("keydown", this.tastatur);
      document.addEventListener("keyup", this.tastatur);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", this.seiteGeht);
      window.addEventListener("blur", this.fensterWeg);
      for (const n of AUFNAHME_EREIGNISSE) window.addEventListener(n, this.aufnahmeBeginnt);
      window.addEventListener(VORLESEN_EREIGNIS, this.vorlesenBeginnt);
    }
    void this.refresh();
  }

  /** Still werden: eine laufende Übung pausiert, ein gehaltener Druck wird verworfen, der Ton endet. Wirft nie. */
  ruhe(grund = "seite", gehtWeg = false): void {
    // Zuerst die Hörspur: sonst sähe sie das Stoppen des Spielers unten als „ein anderer hat ihn genommen“.
    this.hoerspur.stille(grund);
    // Geht die Seite, läuft die Tagesaufnahme SOFORT weiter — auch wenn die Hörspur schon aus war und nur der Nachlauf
    // des Halts noch wartete (danach läuft womöglich keine Uhr mehr).
    if (gehtWeg || grund === "zerstoert") this.halt.lasse(`anlass=${grund}`, { sofort: true });
    for (const u of [this.hoeren, this.geben]) {
      try {
        u?.ruhe(grund, gehtWeg);
      } catch {
        /* egal */
      }
    }
    try {
      this.spieler.stopp();
    } catch {
      /* egal */
    }
  }

  /**
   * Den Stand holen. Beim ersten Mal wird die Seite gebaut; danach (Menü öffnet die Seite erneut) nur der Stand
   * neu gezeichnet — eine pausierte Runde bleibt erhalten.
   */
  async refresh(): Promise<void> {
    if (this.gebaut) return this.aktualisiere();
    this.root.textContent = "";
    const wrap = el("div", "morse-wrap");
    this.root.appendChild(wrap);
    // Ohne Untertitel (Prüfbefund K12): der gemeinsame Kopf zieht seine Linie über die ganze Spalte. Die EINE
    // Kopf-Aktion: „Wie funktioniert das?“ (am Telefon nach der Kopf-Norm in der zweiten Kopfzeile rechts).
    // Die Vorführung steht oben in „Wie funktioniert das?“ (ein Platz wie der in Hören/Bereit) — sichtbar, sobald der
    // Stand da ist (die Liste folgt seiner Stufe und seinem Tempo).
    this.einfuehrungsPlatz ??= this.hoerspur.vorfuehrungPlatz("einfuehrung", () => this.stand);
    const platz = this.einfuehrungsPlatz;
    this.einfuehrung ??= new MorseEinfuehrung({ hoerspur: platz.wurzel, beiZu: () => this.hoerspur.stilleFuer(platz, "zu") });
    wrap.appendChild(buildPageHeader("Morse", () => {}, { action: this.einfuehrung.knopf }));
    wrap.appendChild(this.einfuehrung.wurzel);
    const laden = el("p", "morse-hinweis", "Lade deinen Stand …");
    laden.setAttribute("role", "status");
    wrap.append(laden);
    const r = await this.anfrage<MorseStandDaten>(MORSE_PFADE.stand);
    if (this.zerstoert) return;
    laden.remove();
    const fehler = !r.ok ? r.fehler : istStand(r.daten) ? null : "Der Server antwortet nicht wie erwartet.";
    if (!r.ok || fehler !== null) {
      this.spur(`laden fehler=${(fehler ?? "").replace(/\s+/g, "-").slice(0, 40)}`);
      const karte = el("section", "morse-karte morse-fehler");
      karte.setAttribute("role", "alert");
      karte.append(el("p", "morse-hinweis", `Dein Stand ist nicht geladen: ${fehler}`), knopf("Erneut versuchen", "morse-knopf", () => void this.refresh()));
      wrap.appendChild(karte);
      return;
    }
    this.stand = r.daten;
    this.baue(wrap);
    this.gebaut = true;
  }

  destroy(): void {
    this.zerstoert = true;
    this.hoeren?.zerstoere();
    this.geben?.zerstoere();
    this.ruhe("zerstoert");
    this.hoerspur.zerstoere();
    this.beobachter?.disconnect();
    this.beobachter = null;
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.sichtbarkeit);
      document.removeEventListener("keydown", this.tastatur);
      document.removeEventListener("keyup", this.tastatur);
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("pagehide", this.seiteGeht);
      window.removeEventListener("blur", this.fensterWeg);
      for (const n of AUFNAHME_EREIGNISSE) window.removeEventListener(n, this.aufnahmeBeginnt);
      window.removeEventListener(VORLESEN_EREIGNIS, this.vorlesenBeginnt);
    }
    if (this.speicherUhr) clearTimeout(this.speicherUhr);
  }

  /* ── Server ────────────────────────────────────────────────────── */

  private async anfrage<T>(
    pfad: string,
    init?: { method: string; body: unknown; keepalive?: boolean },
  ): Promise<{ ok: true; daten: T } | { ok: false; fehler: string }> {
    try {
      const r = await this.holen(pfad, {
        method: init?.method ?? "GET",
        credentials: "same-origin",
        headers: init ? { "content-type": "application/json" } : undefined,
        body: init ? JSON.stringify(init.body) : undefined,
        // Geht die Seite gerade (verdeckt, pagehide), soll die Buchung das überleben (Prüfbefund W4).
        keepalive: init?.keepalive === true ? true : undefined,
      });
      if (r.status === 401) return { ok: false, fehler: "Du bist nicht angemeldet." };
      if (r.status === 403) return { ok: false, fehler: "Die Morse-Seite ist für dieses Konto noch nicht freigegeben." };
      if (!r.ok) return { ok: false, fehler: `Der Server antwortet nicht wie erwartet (${r.status}).` };
      return { ok: true, daten: (await r.json()) as T };
    } catch {
      return { ok: false, fehler: "Keine Verbindung zum Server." };
    }
  }

  private async aktualisiere(): Promise<void> {
    // Läuft oder ruht eine Übung, bleibt alles, wie es ist — sie liest Tempo und Ton aus dem Stand.
    if (this.hoeren?.laeuft || this.hoeren?.pausiert || this.geben?.laeuft || this.geben?.pausiert) return;
    const r = await this.anfrage<MorseStandDaten>(MORSE_PFADE.stand);
    if (this.zerstoert) return;
    if (!r.ok) {
      this.spur(`laden fehler=${r.fehler.replace(/\s+/g, "-").slice(0, 40)}`);
      this.meldeStand(`Stand nicht aktualisiert: ${r.fehler}`);
      return;
    }
    this.meldeStand("");
    if (istStand(r.daten)) this.neuerStand(r.daten);
  }

  /** Eine Übung buchen; der Stand der Seite folgt der Antwort. */
  private async buche(
    art: MorseBuchungsArt,
    aufgaben: Array<{ soll: string; ist: string }>,
    dauerMs: number,
    o: { kennung?: string; keepalive?: boolean; umfang?: Umfang } = {},
  ): Promise<Buchung> {
    const e = this.stand!.einstellungen;
    const r = await this.anfrage<BuchungsAntwort>(MORSE_PFADE.sitzung, {
      method: "POST",
      body: { art, aufgaben, dauerMs, tempo: e.tempo, wirksam: e.wirksam, kennung: o.kennung, umfang: o.umfang },
      keepalive: o.keepalive,
    });
    if (this.zerstoert || !r.ok) return r;
    // Gebucht ist gebucht — nur ein mitgeschickter, brauchbarer Stand ersetzt den der Seite (B1).
    const stand = istStand(r.daten?.stand) ? r.daten.stand : null;
    this.spur(`gebucht reiter=${art} aufgaben=${aufgaben.length}${r.daten?.aufgestiegen ? ` stufe=${r.daten.stufe}` : ""}${stand ? "" : " stand=fehlt"}`);
    if (stand) this.neuerStand(stand);
    return r;
  }

  private neuerStand(s: MorseStandDaten): void {
    this.stand = s;
    this.hoerspur.aktualisiere();
    this.zeichneStand();
    this.zeichneReglerWerte();
    this.fortschritt?.zeichne(s);
    if (this.reiter === "tabelle") this.tabelle?.zeichne();
    this.hoeren?.standGeaendert();
    this.geben?.standGeaendert();
  }

  private spur(text: string): void {
    try {
      this.spurZeile(text);
    } catch {
      /* Diagnose ist nie wichtiger als die Übung */
    }
  }

  /** Einen Text mit den Einstellungen spielen (`langsam`: 60 % Tempo). `beimStart`: der Plan liegt auf der Uhr. */
  private spiele(text: string, langsam = false, beimStart?: BeimStart): Promise<MorseSpielErgebnis> {
    // Wer hier ein Zeichen oder eine Gruppe spielt, braucht den Ton — die Hörspur schweigt (Antippen, Übung).
    this.hoerspur.stille("tippen");
    const e = this.stand!.einstellungen;
    // „CH“ aus der Tabelle ist EIN Zeichen (----) — wie das Bild daneben und wie in der Hörspur.
    const plan = zeitplan(text, langsam ? langsamesTempo(e) : e, { ch: text === "CH" });
    return this.spieler.spiele(plan, { ton: e.ton, beimStart: beimStart ? (inMs) => beimStart(plan, inMs) : undefined });
  }

  /** „Augen zu“: der kurze Quittungston — hell (richtig) oder tief (falsch) — über denselben Spieler wie die Zeichen. */
  private quittung(art: QuittungsArt): Promise<MorseSpielErgebnis> {
    this.hoerspur.stille("tippen");
    return this.spieler.spiele(quittungPlan(art), { ton: quittungTon(art, this.stand!.einstellungen.ton), lautstaerke: art === "weiter" ? WEITER_LAUTSTAERKE : undefined });
  }

  /** Ein einzelnes Zeichen zum Anhören (Stand, Fortschritt, Probe) — kam kein Ton, steht es in `wo`. */
  private hoereZeichen(text: string, wo: HTMLElement, stelle: string): void {
    void hoereMitMeldung(this.umgebung(), text, (t) => meldeIn(wo, t, "fehler"), stelle);
  }

  private umgebung(): MorseUmgebung {
    return {
      stand: () => this.stand!,
      spieler: this.spieler,
      jetzt: this.jetzt,
      zufall: this.zufall,
      spur: (t) => this.spur(t),
      spiele: (t, langsam, beimStart) => this.spiele(t, langsam, beimStart),
      buche: (art, aufgaben, dauerMs, o) => this.buche(art, aufgaben, dauerMs, o),
      zeigeTabelle: () => this.waehle("tabelle"),
      vorgabeNehmen: () => this.vorgabeNehmen(),
      stufe: this.stufeAnschluss,
      rundeOffen: () => this.rundeOffen(),
      hoerspur: this.hoerspur,
      sprechen: this.diktat,
      quittung: (art) => this.quittung(art),
      feiere: (d) => this.feiere(d),
      stimme: this.stimmeAnschluss,
      messe: (t) => {
        try {
          this.messeZeile(t);
        } catch {
          /* Diagnose ist nie wichtiger als die Übung */
        }
      },
    };
  }

  /** Läuft oder ruht gerade eine Runde (Hören oder Geben)? */
  private rundeOffen(): boolean {
    return [this.hoeren, this.geben].some((u) => u !== null && (u.laeuft || u.pausiert));
  }

  /** Mitten in einer Runde (läuft oder ruht) bleibt die Stufe, wie sie ist — der Satz sagt warum, sonst `null`. */
  private stufeSperre(): string | null {
    return this.rundeOffen() ? STUFE.sperre : null;
  }

  /** Die Übungsstufe auf dem Server setzen; der Stand der Seite folgt der Antwort. */
  private async stufeSetzen(stufe: number): Promise<StufeErgebnis> {
    const sperre = this.stufeSperre();
    if (sperre !== null) return { ok: false, fehler: sperre };
    const vorher = this.stand?.stufe;
    const r = await this.anfrage<MorseStandDaten>(MORSE_PFADE.stufe, { method: "PUT", body: { stufe } });
    if (!r.ok) {
      this.spur(`stufe fehler=${r.fehler.replace(/\s+/g, "-").slice(0, 40)}`);
      return r;
    }
    if (!istStand(r.daten)) {
      this.spur("stufe fehler=antwort");
      return { ok: false, fehler: "Der Server antwortet nicht wie erwartet." };
    }
    this.spur(`stufe von=${vorher} auf=${r.daten.stufe}`);
    if (!this.zerstoert) this.neuerStand(r.daten);
    return { ok: true };
  }

  /* ── Aufbau ────────────────────────────────────────────────────── */

  private baue(wrap: HTMLElement): void {
    // 23.10.2026 (Martin: „mehr Ordnung … und eine Progressbar“): EINE Karte für den Stand — oben die Zeile (Stufe, neues
    // Zeichen), darunter die Leiste der Stufen, darunter das Ziel der Stufe mit seinen vier Balken (morse-ziel.ts).
    const karte = el("section", "morse-standkarte");
    karte.setAttribute("aria-label", "Dein Stand");
    this.standZeile = el("div", "morse-stand");
    this.stufenPlatz = el("div", "morse-stufenleiste-platz");
    // 16.10.2026: das Ziel der Stufe (morse-ziel.ts) und, nach einem Aufstieg, die Karte dazu — gleich unter dem Stand.
    this.zielPlatz = el("div", "morse-stufenziel-platz");
    karte.append(this.standZeile, this.stufenPlatz, this.zielPlatz);
    this.aufstiegPlatz = el("div", "morse-aufstieg-platz");
    wrap.append(karte, this.aufstiegPlatz);
    this.standMeldung = baueMeldung();
    wrap.appendChild(this.standMeldung);
    this.zeichneStand();

    const leiste = el("div", "morse-tabs");
    leiste.setAttribute("role", "tablist");
    leiste.setAttribute("aria-label", "Üben");
    wrap.appendChild(leiste);
    for (const r of REITER) {
      const b = el("button", "morse-tab", r.titel);
      b.type = "button";
      b.id = `morse-tab-${r.id}`;
      b.setAttribute("role", "tab");
      b.setAttribute("aria-controls", `morse-tafel-${r.id}`);
      b.addEventListener("click", () => this.waehle(r.id));
      b.addEventListener("keydown", (ev) => this.reiterTaste(ev, r.id));
      leiste.appendChild(b);
      this.reiterKnoepfe.set(r.id, b);
      const tafel = el("section", "morse-tafel");
      tafel.id = `morse-tafel-${r.id}`;
      tafel.setAttribute("role", "tabpanel");
      tafel.setAttribute("aria-labelledby", b.id);
      // Was man hier tut — ein, zwei Sätze, gleich unter der Reiter-Leiste (morse-texte.ts).
      const text = el("p", "morse-reiter-text", REITER_TEXT[r.id]);
      text.id = `morse-reiter-text-${r.id}`;
      tafel.setAttribute("aria-describedby", text.id);
      tafel.appendChild(text);
      // ⚠️ Kein tabIndex auf der Tafel: ein Klick in eine fokussierbare Tafel ließ die Leertaste bei app.ts
      //    landen (Sprechtaste), und die Tafeln haben eigene Bedienelemente.
      wrap.appendChild(tafel);
      this.tafeln.set(r.id, tafel);
    }
    const u = this.umgebung();
    this.hoeren = new MorseHoeren(u, this.tafeln.get("hoeren")!);
    this.geben = new MorseGeben(u, this.tafeln.get("geben")!);
    this.fortschritt = new MorseFortschritt(this.tafeln.get("fortschritt")!, (z, wo) => this.hoereZeichen(z, wo, "fortschritt"), this.stufeAnschluss);
    this.fortschritt.zeichne(this.stand!);
    this.hoerspur.aktualisiere();
    this.tabelle = new MorseTabelle(this.tafeln.get("tabelle")!, u, () => this.zurueckAusTabelle());
    wrap.appendChild(this.baueEinstellungen());
    this.waehle(this.reiter, false);
  }

  /** Pfeiltasten wechseln den Reiter (WAI-ARIA Tabs). */
  private reiterTaste(ev: KeyboardEvent, aktuell: MorseReiter): void {
    const i = REITER.findIndex((r) => r.id === aktuell);
    let ziel = -1;
    if (ev.key === "ArrowRight") ziel = (i + 1) % REITER.length;
    else if (ev.key === "ArrowLeft") ziel = (i - 1 + REITER.length) % REITER.length;
    else if (ev.key === "Home") ziel = 0;
    else if (ev.key === "End") ziel = REITER.length - 1;
    if (ziel < 0) return;
    ev.preventDefault();
    this.waehle(REITER[ziel].id);
  }

  private waehle(id: MorseReiter, fokus = true): void {
    const alt = this.reiter;
    if (id !== alt) {
      // Die Hörspur gehört dem Reiter, in dem sie begann: ein Wechsel hält sie an (vor allem anderen Stoppen).
      this.hoerspur.stille("reiter");
      const u = this.uebung(alt);
      // In die Tabelle aus einer Übung: merken, woher — und ob sie lief (dann geht es dort weiter).
      if (id === "tabelle") this.herkunft = alt === "hoeren" || alt === "geben" ? { reiter: alt, weiter: u?.laeuft === true } : null;
      // Wer den Reiter verlässt, pausiert seine Übung; ein gehaltener Druck wird verworfen (Prüfbefund K3).
      u?.ruhe("reiter");
      // Jeder Reiter spielt auch einzelne Zeichen (Tabelle, Fortschritt, Kennenlernen, Anhören) — beim Wechsel
      // verstummt IMMER alles (Prüfbefund K2: das Kennenlernen spielte in der Tabelle weiter).
      this.tabelle?.still();
      try {
        this.spieler.stopp();
      } catch {
        /* egal */
      }
    }
    this.reiter = id;
    try {
      localStorage.setItem(REITER_SCHLUESSEL, id);
    } catch {
      /* egal */
    }
    for (const [rid, b] of this.reiterKnoepfe) {
      const an = rid === id;
      b.setAttribute("aria-selected", String(an));
      b.dataset.active = String(an);
      b.tabIndex = an ? 0 : -1;
      if (an && fokus) b.focus();
      this.tafeln.get(rid)!.hidden = !an;
    }
    // „Üben mit“ gilt für Hören UND Geben (am Gerät gemerkt): wer einen der beiden Reiter zeigt, liest die Wahl neu.
    if ((id === "hoeren" || id === "geben") && id !== alt) this.uebung(id)?.standGeaendert();
    if (id === "fortschritt" && this.stand) this.fortschritt?.zeichne(this.stand);
    if (id === "tabelle") {
      this.tabelle?.zeichne();
      const h = this.herkunft;
      // „Zurück zur Übung“ nur, wenn sie lief (ein Tipp führt hin UND setzt fort). War sie nicht aktiv, leistet der Reiter
      // „Hören“/„Geben“ darüber dasselbe — ein zweiter Knopf dafür wäre nur Dopplung (Martin 08.10.: „so unaufgeräumt“).
      this.tabelle?.setzeZurueck(h?.weiter ? TABELLE.zurueckWeiter : null);
    }
  }

  /** „Zurück zu …“ aus der Tabelle: in den Reiter, aus dem man kam — lief dort die Übung, geht sie weiter. */
  private zurueckAusTabelle(): void {
    const h = this.herkunft;
    if (!h) return;
    this.herkunft = null;
    this.waehle(h.reiter);
    if (h.weiter) this.setzeUebungFort(h.reiter);
  }

  /** Eine pausierte Übung geht weiter — aus der Geste des Tipps: so darf der Ton am iPhone gleich wieder anlaufen. */
  private setzeUebungFort(reiter: "hoeren" | "geben"): void {
    if (this.uebung(reiter)?.pausiert) (reiter === "hoeren" ? this.hoeren : this.geben)?.weiter();
  }

  /**
   * Die Hörspur beginnt: lief im offenen Reiter eine Übung, pausiert sie (wie beim Schnellzugriff „Tabelle“). Der
   * Dienst zeigt dann „Zurück zur Übung“ — der Weg dorthin zurück und weiter.
   */
  private pausiereUebung(): { zurueck: () => void; wartet: () => boolean } | null {
    const reiter = this.reiter;
    if (reiter !== "hoeren" && reiter !== "geben") return null;
    if (!this.uebung(reiter)?.laeuft) return null;
    this.uebung(reiter)?.ruhe("hoerspur");
    return {
      zurueck: () => {
        this.waehle(reiter);
        this.setzeUebungFort(reiter);
      },
      wartet: () => this.uebung(reiter)?.pausiert === true,
    };
  }

  private uebung(id: MorseReiter = this.reiter): MorseUebung | null {
    return id === "hoeren" ? this.hoeren : id === "geben" ? this.geben : null;
  }

  /** Leertaste und Esc für die Übung des offenen Reiters — vor app.ts (Sprechtaste auf der Leertaste). */
  private taste(ev: KeyboardEvent): void {
    if (ev.defaultPrevented || ev.altKey || ev.ctrlKey || ev.metaKey || ev.isComposing) return;
    if (!this.gebaut || this.zerstoert || this.root.hidden || !this.root.isConnected) return;
    const ziel = ev.target;
    const imBereich = ziel === document.body || ziel === document.documentElement || (ziel instanceof Node && this.root.contains(ziel));
    if (!imBereich) return;
    // Esc hält zuerst die Hörspur an (sie lief ja, ohne dass eine Übung läuft); ein zweites Esc dann die Übung.
    if (ev.key === "Escape" && this.hoerspur.engine.aktiv) {
      if (ev.type === "keydown") {
        ev.preventDefault();
        ev.stopPropagation();
        this.hoerspur.engine.stopp("esc");
      }
      return;
    }
    const u = this.uebung();
    if (!u) return;
    if (ev.key === "Escape") {
      if (ev.type === "keydown" && (u.laeuft || u.pausiert)) {
        ev.preventDefault();
        ev.stopPropagation();
        u.stopp("esc");
      }
      return;
    }
    // Auf dem offenen Reiter heißt die Leertaste „Start“ (W3: sonst stimmte der Hinweis nicht, sobald man den Reiter
    // angeklickt hat) — er ist ja schon offen. Andere Bedienelemente behalten ihre Leertaste.
    const aufOffenemReiter = ziel instanceof Element && ziel.getAttribute("role") === "tab" && ziel.getAttribute("aria-selected") === "true";
    if (ev.key !== " " || (!aufOffenemReiter && bedientLeertasteSelbst(ziel))) return;
    const verbraucht = u.leertaste(ev.type === "keyup" ? "keyup" : "keydown", ev.repeat);
    // Nur das DRÜCKEN gehört der Seite (sonst startete app.ts damit die Sprechtaste). Das Loslassen wird NIE
    // verschluckt (Prüfbefund K3) — wer es braucht, soll es bekommen.
    if (verbraucht && ev.type === "keydown") {
      ev.preventDefault();
      ev.stopPropagation();
    }
  }

  /* ── Stand (oben, eine Zeile) ──────────────────────────────────── */

  private zeichneStand(): void {
    const s = this.stand!;
    const k = this.standZeile;
    k.textContent = "";
    const stufe = el("p", "morse-stand-stufe");
    stufe.append(el("span", "morse-stand-wort", "Stufe"), el("span", "morse-stand-zahl", String(s.stufe)), el("span", "morse-stand-von", `von ${KOCH_LETZTE_STUFE}`));
    // Neu sind auf Stufe 1 BEIDE ersten Zeichen (K und M) — wie in Tabelle, Kennenlernen und Fortschritt.
    const neue = kochNeueZeichen(s.stufe);
    const neuKnopf = el("button", "morse-stand-neu");
    neuKnopf.type = "button";
    neuKnopf.setAttribute(
      "aria-label",
      `${neue.length > 1 ? "Neue Zeichen" : "Neues Zeichen"} ${neue.map((z) => `${z}: ${musterSprechen(MORSE_TABELLE[z])}`).join(", ")} — anhören`,
    );
    neuKnopf.append(el("span", "morse-stand-neu-wort", "neu"));
    for (const z of neue) {
      const bild = musterBild(MORSE_TABELLE[z], "s");
      bild.classList.add("morse-stand-neu-muster");
      bild.setAttribute("aria-hidden", "true");
      neuKnopf.append(el("span", "morse-stand-neu-zeichen", z), bild);
    }
    neuKnopf.addEventListener("click", () => this.hoereZeichen(neue.join(" "), this.standMeldung, "stand"));
    // 16.10.2026: Rechts stand „sicher 75 %“ — das Mittel der Güte aller Zeichen. Es sagt nichts darüber, ob die Stufe
    // steigt (Martin übte 200 Mal, die Zahl blieb). Mit Fenster steht hier das Ziel („Ziel 2/4“), darunter der Satz, was
    // fehlt; die Güte je Zeichen bleibt im Fortschritt. Ohne Fenster (älterer Server) bleibt die alte Anzeige.
    const ziel = zielAnsicht(s);
    if (ziel) {
      // Die Zahl des Ziels („2/4 erfüllt · 61 %“) steht seit 23.10.2026 über den vier Balken (morse-ziel.ts).
      k.append(stufe, neuKnopf);
    } else {
      const sicher = stufenSicherheit(s.stufe, s.zeichen);
      const sicherheit = el("p", "morse-stand-sicher");
      const balken = gueteBalken("morse-stand-balken", sicher ?? 0);
      sicherheit.append(el("span", "morse-stand-sicher-wort", "sicher"), balken, el("span", "morse-stand-sicher-wert", sicher === null ? "–" : prozent(sicher)));
      sicherheit.setAttribute("aria-label", sicher === null ? "Sicherheit: noch nicht geübt" : `Mittlere Sicherheit deiner Zeichen: ${prozent(sicher)}`);
      k.append(stufe, neuKnopf, sicherheit);
    }
    const leiste = baueStufenLeiste(s.stufe);
    this.stufenPlatz.replaceChildren(...(leiste ? [leiste] : []));
    const warOffen = this.zielPlatz.querySelector<HTMLDetailsElement>(".morse-stufenziel")?.open ?? false;
    this.zielPlatz.textContent = "";
    if (ziel) this.zielPlatz.appendChild(baueZiel(ziel, warOffen));
  }

  /**
   * Eine Stufe geschafft (16.10.2026, Martin: eine kleine Feier). Gerufen von Hören und „Augen zu“ an der Stelle, an der
   * sie nach dem Ende der Runde „Stufe N!“ melden — nie mitten in der Runde (das Mikrofon hört bei „Augen zu“ mit).
   * Eine ruhige Karte unter dem Stand (neues Zeichen, Morse-Bild, Merkwort) und eine kurze Tonfolge über den Ton-Weg der
   * App (audio-feedback.ts, so leise wie die Quittungen). Notschalter: localStorage `vespera.morse.fanfare` = „aus“.
   */
  private feiere(d: BuchungsAntwort): void {
    this.aufstiegPlatz.textContent = "";
    this.aufstiegPlatz.appendChild(baueAufstiegKarte(d.stufe, () => (this.aufstiegPlatz.textContent = "")));
    this.spur(`aufstieg stufe=${d.stufe}`);
    if (fanfareErlaubt()) {
      this.hoerspur.stille("aufstieg");
      playBeep("aufstieg");
    }
  }

  private meldeStand(text: string): void {
    meldeIn(this.standMeldung, text, "fehler");
  }

  /* ── Tempo und Ton (eingeklappt, für alle Reiter) ──────────────── */

  private baueEinstellungen(): HTMLElement {
    const d = el("details", "morse-einstellungen");
    this.einstellungenBlock = d;
    const kopf = el("summary", "morse-einstellungen-kopf");
    this.einstellungenKurz = el("span", "morse-einstellungen-kurz");
    kopf.append(el("span", "morse-einstellungen-titel", "Tempo und Ton"), this.einstellungenKurz);
    const inhalt = el("div", "morse-einstellungen-inhalt");
    inhalt.append(el("p", "morse-hinweis", TEMPO.erklaerung));
    // Die Vorgabe der Stufe (zum Einstieg langsamer) — und ein Weg zurück dorthin, wenn man verstellt hat.
    const vorgabe = el("div", "morse-vorgabe");
    this.vorgabeZeile = el("p", "morse-hinweis");
    this.vorgabeKnopf = knopf(TEMPO.vorgabeNehmen, "morse-knopf morse-knopf--klein", () => this.vorgabeNehmen());
    vorgabe.append(this.vorgabeZeile, this.vorgabeKnopf);
    inhalt.append(vorgabe);
    const e = this.stand!.einstellungen;
    const regler = (name: keyof MorseEinstellungen, titel: string, min: number, max: number, einheit: string, sprich: string): void => {
      const feld = el("label", "morse-regler");
      const kopfZeile = el("span", "morse-regler-kopf");
      const wert = el("output", "morse-regler-wert", `${e[name]} ${einheit}`);
      kopfZeile.append(el("span", "morse-feld-titel", titel), wert);
      const r = el("input", "morse-regler-eingabe");
      r.type = "range";
      r.min = String(min);
      r.max = String(max);
      r.step = name === "ton" ? "10" : "1";
      r.value = String(e[name]);
      r.setAttribute("aria-valuetext", `${e[name]} ${sprich}`);
      r.addEventListener("input", () => {
        const roh = { ...this.stand!.einstellungen, [name]: Number(r.value) };
        // „wirksam“ folgt dem Zeichentempo nach unten, nie darüber.
        if (roh.wirksam > roh.tempo) roh.wirksam = roh.tempo;
        this.stand!.einstellungen = pruefeMorseEinstellungen(roh);
        this.zeichneReglerWerte();
        this.einstellungenSpeichern();
      });
      feld.append(kopfZeile, r);
      inhalt.appendChild(feld);
      this.regler.set(name, { r, wert, einheit, sprich });
    };
    regler("tempo", "Zeichentempo", MORSE_GRENZEN.tempoMin, MORSE_GRENZEN.tempoMax, "WpM", "Wörter pro Minute");
    regler("wirksam", "Wirksames Tempo", MORSE_GRENZEN.tempoMin, MORSE_GRENZEN.tempoMax, "WpM", "Wörter pro Minute");
    regler("ton", "Tonhöhe", MORSE_GRENZEN.tonMin, MORSE_GRENZEN.tonMax, "Hz", "Hertz");
    this.einstellungenMeldung = baueMeldung();
    const aktionen = el("div", "morse-aktionen");
    aktionen.appendChild(knopf("Probe hören", "morse-knopf", () => this.hoereZeichen("PARIS", this.einstellungenMeldung, "probe")));
    inhalt.append(aktionen, this.einstellungenMeldung);
    d.append(kopf, inhalt);
    this.zeichneReglerWerte();
    return d;
  }

  private zeichneReglerWerte(): void {
    const e = this.stand?.einstellungen;
    if (!e) return;
    for (const [name, x] of this.regler) {
      x.r.value = String(e[name]);
      x.wert.textContent = `${e[name]} ${x.einheit}`;
      x.r.setAttribute("aria-valuetext", `${e[name]} ${x.sprich}`);
    }
    if (this.einstellungenKurz) this.einstellungenKurz.textContent = `${e.tempo} WpM · wirksam ${e.wirksam} · ${e.ton} Hz`;
    if (this.vorgabeZeile && this.stand) {
      const v = kochTempoVorgabe(this.stand.stufe);
      this.vorgabeZeile.textContent = TEMPO.vorgabe(this.stand.stufe, v.tempo, v.wirksam);
      this.vorgabeKnopf.hidden = v.tempo === e.tempo && v.wirksam === e.wirksam;
    }
  }

  /**
   * Das Tempo der Stufe nehmen (Knopf unter „Tempo und Ton“ und beim Hören, K1) — EIN Weg. Nur auf einen Tipp hin:
   * die eigene Wahl wird nie von selbst überschrieben.
   */
  private vorgabeNehmen(): void {
    if (!this.stand) return;
    const v = kochTempoVorgabe(this.stand.stufe);
    this.stand.einstellungen = pruefeMorseEinstellungen({ ...this.stand.einstellungen, ...v });
    this.zeichneReglerWerte();
    this.hoeren?.standGeaendert();
    this.einstellungenSpeichern();
  }

  /** Nach einer kurzen Ruhe speichern — nicht bei jedem Schritt des Reglers. */
  private einstellungenSpeichern(): void {
    if (this.speicherUhr) clearTimeout(this.speicherUhr);
    this.speicherUhr = setTimeout(() => {
      this.speicherUhr = null;
      void (async () => {
        const m = this.einstellungenMeldung;
        const r = await this.anfrage<MorseStandDaten>(MORSE_PFADE.einstellungen, { method: "PUT", body: this.stand!.einstellungen });
        if (this.zerstoert) return;
        if (!r.ok) {
          this.spur(`einstellungen fehler=${r.fehler.replace(/\s+/g, "-").slice(0, 40)}`);
          meldeIn(m, `Tempo und Ton nicht gespeichert: ${r.fehler}`, "fehler");
          // Kam der Wunsch von woanders (Knopf „Vorgabe nehmen“ beim Hören), soll man den Fehler trotzdem sehen.
          this.einstellungenBlock.open = true;
          return;
        }
        // Läuft eine Übung, gilt das Neue ab der nächsten Gruppe — der Stand selbst bleibt bis dahin.
        const laeuft = this.hoeren?.laeuft === true || this.geben?.laeuft === true;
        if (!istStand(r.daten)) {
          meldeIn(m, "Tempo und Ton nicht gespeichert: Der Server antwortet nicht wie erwartet.", "fehler");
          this.einstellungenBlock.open = true;
          return;
        }
        if (laeuft) this.stand!.einstellungen = r.daten.einstellungen;
        else this.neuerStand(r.daten);
        meldeIn(m, "Tempo und Ton gespeichert.");
      })();
    }, 600);
  }
}
